import { Text } from "@asayinc/component-library";
import { Box, Stack } from "@mui/material";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";

import { Card } from "src/components/atoms/Card";
import { ErrorState } from "src/components/atoms/ErrorState";
import { ScrollContainer } from "src/components/containers/ScrollContainer";
import { LabelField } from "src/components/molecules/LabelField";
import {
  LoadingCard,
  LoadingStatsHeaderCard,
} from "src/components/molecules/Loading";
import { LOCATION_ANALYSIS_EVENT_LABEL } from "src/constants/labels/locationAnalysisEventLabel";
import { useLocationAnalysisEventQuery } from "src/store/locationAnalysisEvent";

export function LocationAnalysisEventDetailsTab() {
  const { id } = useParams() as { id: string };
  const {
    data: locationAnalysisEvent,
    isLoading,
    error,
  } = useLocationAnalysisEventQuery({ id });

  if (error || (!isLoading && !locationAnalysisEvent)) {
    return <ErrorState />;
  }

  if (isLoading) {
    return (
      <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
        <LoadingStatsHeaderCard />
        <Box sx={{ display: "flex", mt: 8 }}>
          <Box sx={{ flexGrow: 1, flexBasis: 0, mr: 12 }}>
            <LoadingCard />
          </Box>
        </Box>
      </ScrollContainer>
    );
  }

  const {
    securities,
    recordDate,
    type,
    jobNumber,
    solicitor,
    solicitorContact,
    created,
    scheduledGenerationAt,
    generatedAt,
  } = locationAnalysisEvent;

  return (
    <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
      <Card title="Location Analysis Event Details">
        <Stack sx={{ mt: 6, px: 6 }} direction="row">
          <Box sx={{ width: "25%" }}>
            <Text variant="subtitle1" sx={{ mb: 3 }}>
              Type
            </Text>
            <Box sx={{ display: "flex", alignItems: "baseline" }}>
              <Text variant="h5">{LOCATION_ANALYSIS_EVENT_LABEL[type]}</Text>
            </Box>
          </Box>
          <Box sx={{ width: "25%" }}>
            <Text variant="subtitle1" sx={{ mb: 3 }}>
              CUSIPs
            </Text>
            <Text variant="h5">
              {securities.map((security) => security.cusip).join(", ")}
            </Text>
          </Box>
          <Box sx={{ width: "25%" }}>
            <Text variant="subtitle1" sx={{ mb: 3 }}>
              Record Date
            </Text>
            <Box sx={{ display: "flex", alignItems: "baseline" }}>
              <Text variant="h5">{dayjs(recordDate).format("MM/DD/YYYY")}</Text>
            </Box>
          </Box>
          <Box sx={{ width: "25%" }}>
            <Text variant="subtitle1" sx={{ mb: 3 }}>
              Job Number
            </Text>
            <Text variant="h5">{jobNumber || "-"}</Text>
          </Box>
        </Stack>
      </Card>
      <Box sx={{ display: "flex", mt: 8 }}>
        <Box sx={{ flexGrow: 1, flexBasis: 0, maxWidth: "50%" }}>
          <Card title="Notes">
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              label="Created"
              isLabelBold={true}
              value={dayjs(created).format("MM/DD/YYYY")}
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              label="Scheduled Generation"
              isLabelBold={true}
              value={
                scheduledGenerationAt
                  ? dayjs(scheduledGenerationAt).format("MM/DD/YYYY HH:mm")
                  : "-"
              }
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              label="Generated"
              isLabelBold={true}
              value={
                generatedAt
                  ? dayjs(generatedAt).format("MM/DD/YYYY HH:mm")
                  : "-"
              }
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              label="Solicitor"
              isLabelBold={true}
              value={solicitor.name}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Solicitor Contact"
              tooltip="The email address of the employee who will be receiving the completed location analysis event reports."
              value={solicitorContact.email}
            />
          </Card>
        </Box>
      </Box>
    </ScrollContainer>
  );
}
